import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Seo, { CONTACT_EMAIL, SITE_NAME, SITE_URL } from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dahc96jp14a1h3mr6if0';
const MAX_MESSAGE = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

const topics = ['投稿玩梗现场', '内容纠错与反馈', '合作与内容授权', '其他事情'];

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete('contact_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (!isOk) {
        setStatus('error');
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : '提交没有成功，请稍后再试，或直接发邮件联系我们。',
        );
        return;
      }

      setStatus('success');
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus('error');
      setFormError('网络好像不太稳定，请稍后重试，或直接发邮件联系我们。');
    }
  };

  const contactJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: `联系我们 | ${SITE_NAME}`,
      url: `${SITE_URL}/contact`,
      inLanguage: 'zh-CN',
      description: '通过表单或邮件联系我们，投稿玩梗现场、反馈内容问题或洽谈合作。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: '联系我们', item: `${SITE_URL}/contact` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`联系我们 | ${SITE_NAME} - 投稿玩梗现场与反馈`}
        description="想投稿玩梗现场、反馈内容问题或洽谈合作？通过糖心吃瓜的联系表单或邮件告诉我们，我们通常在两到三个工作日内回复。"
        keywords="糖心吃瓜联系,投稿玩梗,内容反馈,合作洽谈,联系方式"
        path="/contact"
        jsonLd={contactJsonLd}
      />

      <Navbar />

      <main className="pb-16 pt-24 md:pt-28">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Breadcrumb items={[{ label: '联系我们' }]} />

          <header className="mt-6 max-w-3xl">
            <h1 className="text-3xl text-foreground-950 md:text-5xl">联系我们</h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              不管是刷到好玩的玩梗现场想投稿，还是发现站内某条内容需要修正，
              都欢迎直接告诉我们。表单提交后我们会在两到三个工作日内查看并回复。
            </p>
          </header>

          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_340px]">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="text-xl text-foreground-950 md:text-2xl">填写投稿 / 反馈表单</h2>
              <p className="mt-2 text-sm text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项，其余可以按需填写。
              </p>

              {status === 'success' && (
                <div
                  role="status"
                  className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
                >
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-600" aria-hidden="true" />
                  <span>提交成功，我们已经收到你的信息，会尽快查看并回复你留下的邮箱。</span>
                </div>
              )}

              {status === 'error' && (
                <div
                  role="alert"
                  className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800"
                >
                  <i className="ri-error-warning-line mt-0.5 text-lg text-primary-600" aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              )}

              <form
                id="tangxin-contact-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="mt-6 space-y-5"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nickname" className="block text-sm text-foreground-800">
                      怎么称呼你 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="nickname"
                      name="nickname"
                      type="text"
                      required
                      autoComplete="nickname"
                      placeholder="昵称或称呼都可以"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="用于接收我们的回复"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="topic" className="block text-sm text-foreground-800">
                    这次想聊什么 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    required
                    defaultValue={topics[0]}
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    {topics.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-foreground-800">
                    具体内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={MAX_MESSAGE}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="简单描述一下这段玩梗现场，或者你想反馈的问题，越具体越好。"
                    className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                  <p className="mt-1.5 text-right text-xs text-foreground-500">
                    {messageLength} / {MAX_MESSAGE}
                  </p>
                </div>

                <div className="form-anti-bot-field" aria-hidden="true">
                  <label htmlFor="contactAlt">请勿填写此字段</label>
                  <input
                    id="contactAlt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="flex flex-col gap-4 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-foreground-500">
                    提交即表示你同意我们仅将信息用于本次沟通，不会用于其他用途。
                  </p>
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i
                      className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}
                      aria-hidden="true"
                    />
                    {status === 'submitting' ? '正在提交…' : '提交信息'}
                  </button>
                </div>
              </form>
            </section>

            <aside className="space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="m-0 flex items-center gap-2 text-base text-foreground-950">
                  <i className="ri-mail-send-line text-primary-500" aria-hidden="true" />
                  直接发邮件
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  如果不方便填表，也可以直接发邮件给我们，请在标题里简单写明来意。
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  rel="nofollow"
                  className="mt-4 flex items-center gap-2 break-all rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-mail-line" aria-hidden="true" />
                  {CONTACT_EMAIL}
                </a>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="m-0 text-base text-foreground-950">回复时间</h2>
                <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                  <li className="flex items-center justify-between gap-3">
                    <span>投稿玩梗现场</span>
                    <span className="text-foreground-900">2 个工作日内</span>
                  </li>
                  <li className="flex items-center justify-between gap-3">
                    <span>内容纠错反馈</span>
                    <span className="text-foreground-900">1 个工作日内</span>
                  </li>
                  <li className="flex items-center justify-between gap-3">
                    <span>合作与授权</span>
                    <span className="text-foreground-900">3 个工作日内</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-secondary-950 p-5 text-background-100">
                <h2 className="m-0 flex items-center gap-2 text-base text-background-50">
                  <i className="ri-question-line text-accent-300" aria-hidden="true" />
                  先看看常见问题
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-background-200">
                  关于内容来源、更新频率、投稿方式这些常见疑问，首页的常见问题区域已经做了说明。
                </p>
                <Link
                  to="/"
                  className="mt-4 flex items-center gap-2 text-sm text-accent-300 transition-colors hover:text-accent-200"
                >
                  回到首页查看
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}